import React from 'react'

import SettingsSection from '../../SettingsSection'
import EditDelete from '../IntegrationColumns/EditDelete'
import GetCode from '../IntegrationColumns/GetCode'
import Button from '../../../../components/atoms/Button2'
import IntegrationModal from '../../../../components/molecules/ChatModals/IntegrationModal'
import Table from '../../../../components/molecules/Table'
import NoPermissionContainer from '../../../../components/molecules/NoPermissionContainer'

import { columnHelper } from './helpers'
import { formatDate_MMDDYYYY, maskKey } from '../../../../utilities'
import { useIntegrations } from '../../../../providers/IntegrationProvider'
import { useBreakpoint } from '../../../../providers/BreakpointProvider'
import useStyles from './styles'

const IntegrationsTable = () => {
  const { integrations, loaded, error } = useIntegrations()
  const classes = useStyles()
  const { isExtraSmall, isSmall, isMedium } = useBreakpoint()
  const [showModal, setShowModal] = React.useState(false)
  const [data, setData] = React.useState(null)

  // Opens the modal
  const openModal = React.useCallback(
    data => () => {
      if (data) {
        setData(data)
      }
      setShowModal(true)
    },
    []
  )

  // Closes the modal
  const handleClose = React.useCallback(() => {
    setShowModal(false)
    setData(null)
  }, [])

  const columns = React.useMemo(() => {
    const nameColumn = columnHelper.accessor('name', {
      header: 'Name',
      cell: ({ row }) => (
        <div style={{ cursor: 'default' }}>{row.original.name}</div>
      )
    })

    const createdColumn = columnHelper.accessor('created_at', {
      header: 'Created At',
      cell: ({ row }) => (
        <div style={{ cursor: 'default' }}>
          {formatDate_MMDDYYYY(row.original.created_at)}
        </div>
      )
    })

    const keyColumn = columnHelper.accessor('api_key', {
      header: 'Api Key',
      cell: ({ row }) => (
        <div style={{ cursor: 'default' }}>{maskKey(row.original.api_key)}</div>
      )
    })

    const getCodeColumn = columnHelper.display({
      id: 'code',
      header: 'Get Code',
      cell: ({ row }) => <GetCode id={row.original.id} />
    })

    const widgetEnabledColumn = columnHelper.accessor('widget_config', {
      header: 'Widget',
      cell: ({ row }) => {
        // if object is null, the widget is disabled
        if (Boolean(row.original.widget_config)) {
          return <div style={{ cursor: 'default' }}>Enabled</div>
        }

        return <div style={{ color: 'red', cursor: 'default' }}>Disabled</div>
      }
    })

    const editColumn = columnHelper.display({
      id: 'edit',
      header: 'Edit / Delete',
      cell: ({ row }) => <EditDelete open={openModal} data={row.original} />
    })

    if (isExtraSmall || isSmall) {
      return [nameColumn, editColumn]
    }

    if (isMedium) {
      return [nameColumn, getCodeColumn, widgetEnabledColumn, editColumn]
    }

    return [
      nameColumn,
      createdColumn,
      keyColumn,
      getCodeColumn,
      widgetEnabledColumn,
      editColumn
    ]
  }, [openModal, isExtraSmall, isSmall, isMedium])

  return (
    <NoPermissionContainer skipAgentPage error={error}>
      <SettingsSection title='Settings' loaded={loaded}>
        <Table
          data={Object.values(integrations)}
          columns={columns}
          loaded={loaded}
          message='You have no integrations'
          hideCount
          disablePagination
        />

        <div className={classes.root}>
          <Button onClick={() => setShowModal(true)} className={classes.button}>
            CREATE INTEGRATION
          </Button>
        </div>

        {showModal && (
          <IntegrationModal
            open={showModal}
            onClose={handleClose}
            data={data}
          />
        )}
      </SettingsSection>
    </NoPermissionContainer>
  )
}

export default IntegrationsTable
